import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { mensagemErro } from '../api/axios';
import { candidatosRecuperacao, confirmarRecuperacoes } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  SelecaoBuscavel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { usePaginacao } from '../hooks/usePaginacao';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { brl } from '../utils/formato';

/**
 * A glosa que voltou a ser paga depois de um recurso.
 *
 * A tela é de DECISÃO, não de relatório, e o desenho segue disso: cada linha
 * mostra os dois lados — o que foi recusado e em que mês, o que foi pago e em
 * que mês — porque é a comparação que sustenta o "sim". Um par
 * `(guia, procedimento)` também repete por motivo legítimo (o mesmo exame
 * pedido de novo), e nada no arquivo distingue os dois casos.
 *
 * Por isso nada é aplicado sozinho. O sistema propõe; quem confirma é uma
 * pessoa — a mesma regra que impede consultar o Bioslab pela guia alternativa
 * "por garantia".
 */
export default function Recuperacoes() {
  const [params, setParams] = useSearchParams();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [agindo, setAgindo] = useState(false);
  const [marcados, setMarcados] = useState(() => new Set());

  const consulta = useMemo(() => {
    const q = {};
    ['protocolo', 'operadora'].forEach((c) => {
      const v = params.get(c);
      if (v) q[c] = v;
    });
    return q;
  }, [params]);

  const carregar = useCallback(() => {
    setDados(null);
    candidatosRecuperacao(consulta)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível buscar.')));
  }, [consulta]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const candidatos = dados?.candidatos || [];
  // Os nomes vem do hook, nao de uma API imaginada: `paginados`, `de`,
  // `ate`, `tamanho`, `alterarTamanho`. Desestruturar chave que nao existe
  // compila e so quebra em tempo de execucao — foi o que derrubou esta tela.
  // Ordena a lista inteira antes de fatiar: ordenar a página já cortada
  // ordenaria só as 25 linhas à vista.
  const ord = useOrdenacao(candidatos);
  const pag = usePaginacao(ord.ordenados, 25);

  // As opções saem do próprio resultado: oferecer um filtro que não casa com
  // nada é prometer um recorte que não existe.
  const opcoesDe = (campo) => {
    const vistos = new Set();
    candidatos.forEach((c) => c[campo] && vistos.add(c[campo]));
    return [...vistos].sort().map((v) => ({ valor: v, rotulo: v }));
  };

  const mudarFiltro = (campo, valor) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(campo, valor);
    else p.delete(campo);
    setParams(p);
    setMarcados(new Set());
  };

  const alternar = (id) => {
    const n = new Set(marcados);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setMarcados(n);
  };

  const marcarPagina = () => {
    const ids = pag.paginados.map((c) => c.id_glosa);
    const todos = ids.every((i) => marcados.has(i));
    const n = new Set(marcados);
    ids.forEach((i) => (todos ? n.delete(i) : n.add(i)));
    setMarcados(n);
  };

  const confirmar = async () => {
    const escolhidos = candidatos.filter((c) => marcados.has(c.id_glosa));
    if (!escolhidos.length) return;
    const total = escolhidos.reduce((s, c) => s + Number(c.valor_sugerido), 0);
    const ok = window.confirm(
      `Confirmar ${escolhidos.length} recuperação(ões), somando ` +
        `${brl(total)}?\n\nA glosa desses títulos cai no mesmo valor, e o que ` +
        `está em aberto sobe. O recebido não muda — o arquivo de pagamento já ` +
        `trouxe esse dinheiro.`
    );
    if (!ok) return;

    setAgindo(true);
    setErro('');
    try {
      const r = await confirmarRecuperacoes(
        escolhidos.map((c) => ({
          id_glosa: c.id_glosa,
          id_arquivo_pagamento: c.id_arquivo_pagamento,
          valor: c.valor_sugerido,
        }))
      );
      const n = r.data.confirmadas.length;
      const f = r.data.falharam.length;
      setAviso(
        `${n} recuperação(ões) confirmada(s), somando ${brl(
          r.data.valor_recuperado
        )}.` +
          (f
            ? ` ${f} não passaram: ${r.data.falharam
                .map((x) => x.motivo)
                .slice(0, 2)
                .join(' · ')}`
            : '')
      );
      setMarcados(new Set());
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível confirmar.'));
    } finally {
      setAgindo(false);
    }
  };

  const resumo = dados?.resumo;
  const selecionado = candidatos.filter((c) => marcados.has(c.id_glosa));
  const valorSelecionado = selecionado.reduce(
    (s, c) => s + Number(c.valor_sugerido),
    0
  );

  return (
    <>
      <PageHeader
        titulo="Recuperação de glosa"
        subtitulo="Itens que foram recusados numa competência e pagos em outra, depois do recurso"
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="sucesso" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      <div
        className="mb-3 p-3"
        style={{
          border: '1px solid var(--ff-border-soft)',
          borderRadius: 10,
          background: 'var(--ff-surface-muted)',
          fontSize: 13,
        }}
      >
        <strong>Por que isto precisa de uma pessoa.</strong> Um par
        guia + procedimento repete por motivo legítimo — o mesmo exame pedido
        de novo. O sistema não consegue distinguir os dois casos pelo arquivo,
        e a aritmética do título fecha com ou sem o abatimento, então nenhuma
        conferência apanharia o erro. Confirme olhando os dois lados.
      </div>

      {dados?.ignoradas_por_politica?.length > 0 && (
        <Alerta tipo="info">
          Fora da detecção por política:{' '}
          {dados.ignoradas_por_politica
            .map((x) => `${x.operadora} (${x.itens} ite${x.itens === 1 ? 'm' : 'ns'})`)
            .join(' · ')}
          . Nessas operadoras o recurso aceito gera um protocolo novo, então a
          volta do dinheiro não está dentro do protocolo original.{' '}
          <Link to="/modelos">Rever a política</Link>.
        </Alerta>
      )}

      {resumo && (
        <div className="ff-grid-tiles mb-3">
          <Indicador rotulo="Candidatos" valor={resumo.qtd} />
          <Indicador
            rotulo="Glosa a recuperar"
            valor={brl(resumo.valor_sugerido)}
            natureza="positiva"
            nota="sai da conta de glosa e volta para o em aberto"
          />
          <Indicador rotulo="Títulos afetados" valor={resumo.protocolos} />
          <Indicador
            rotulo="Pagamento maior que a glosa"
            valor={resumo.qtd_excedentes}
            natureza={resumo.qtd_excedentes ? 'atencao' : undefined}
            nota="olhar com mais cuidado: pode ser outro atendimento"
          />
        </div>
      )}

      <Painel>
        <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
          <SelecaoBuscavel
            rotulo="Operadora"
            valor={params.get('operadora') || ''}
            opcoes={opcoesDe('operadora')}
            aoMudar={(v) => mudarFiltro('operadora', v)}
          />
          <SelecaoBuscavel
            rotulo="Protocolo"
            valor={params.get('protocolo') || ''}
            opcoes={opcoesDe('protocolo')}
            aoMudar={(v) => mudarFiltro('protocolo', v)}
          />
          <div className="ms-auto d-flex align-items-center gap-3">
            {selecionado.length > 0 && (
              <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                {selecionado.length} selecionado(s) ·{' '}
                <strong>{brl(valorSelecionado)}</strong>
              </span>
            )}
            <Botao
              onClick={confirmar}
              disabled={agindo || !selecionado.length}
              icone="bi-check2-all"
            >
              Confirmar recuperação
            </Botao>
          </div>
        </div>

        {!dados ? (
          <LoadingSpinner />
        ) : candidatos.length === 0 ? (
          <Vazio
            icone="bi-check2-circle"
            titulo="Nenhum candidato"
            descricao="Nenhum item glosado reapareceu pago numa competência posterior. É o esperado enquanto só houver poucas competências carregadas."
          />
        ) : (
          <>
            <Tabela>
              <thead>
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        pag.paginados.length > 0 &&
                        pag.paginados.every((c) => marcados.has(c.id_glosa))
                      }
                      onChange={marcarPagina}
                      aria-label="Marcar a página"
                    />
                  </Th>
                  <Th campo="protocolo" estado={ord}>Protocolo</Th>
                  <Th campo="guia" estado={ord}>Guia</Th>
                  <Th campo="codigo_servico" estado={ord}>Procedimento</Th>
                  <Th campo="competencia_glosa" estado={ord}>
                    Glosado em
                    <Ajuda titulo="Glosado em">
                      A competência do extrato de glosa em que este item foi recusado.
                    </Ajuda>
                  </Th>
                  <Th campo="valor_glosado" estado={ord} alinhar="right">
                    Valor glosado
                  </Th>
                  <Th campo="competencia_pagamento" estado={ord}>
                    Pago em
                    <Ajuda titulo="Pago em">
                      A competência do demonstrativo posterior em que o mesmo
                      procedimento desta guia aparece pago.
                    </Ajuda>
                  </Th>
                  <Th campo="valor_pago_no_retorno" estado={ord} alinhar="right">
                    Valor pago
                  </Th>
                  <Th campo="valor_sugerido" estado={ord} alinhar="right">
                    A recuperar
                    <Ajuda titulo="A recuperar">
                      O que se abate da glosa. Nunca passa do que foi recusado:
                      pagar mais do que se glosou é outro fato.
                    </Ajuda>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {pag.paginados.map((c) => (
                  <tr key={c.id_glosa}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={marcados.has(c.id_glosa)}
                        onChange={() => alternar(c.id_glosa)}
                        aria-label={`Marcar guia ${c.guia}`}
                      />
                    </td>
                    <td>
                      {c.protocolo ? (
                        <Link
                          to={`/titulos?busca=${encodeURIComponent(c.protocolo)}`}
                          className="ff-mono"
                        >
                          {c.protocolo}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--ff-muted)' }}>—</span>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                        {c.operadora}
                      </div>
                    </td>
                    <td className="ff-mono">{c.guia}</td>
                    <td>
                      <span className="ff-mono">{c.codigo_servico}</span>
                      <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                        {c.descricao_servico || '—'}
                      </div>
                      {c.codigo_causa && (
                        <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                          causa {c.codigo_causa}
                        </div>
                      )}
                    </td>
                    <td className="ff-mono">
                      {c.competencia_glosa}
                      {/* O documento dos DOIS lados, igual à coluna "Pago em":
                          "glosado em 04/2026" não diz de qual dos oito
                          demonstrativos daquele mês a recusa veio, e é esse
                          arquivo que se anexa ao recurso. */}
                      <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                        {c.arquivo_glosa}
                      </div>
                      {!c.politica_conhecida && (
                        <div>
                          <Badge tom="arquivado">política não verificada</Badge>
                        </div>
                      )}
                    </td>
                    <td className="ff-mono text-end">{brl(c.valor_glosado)}</td>
                    <td className="ff-mono">
                      {c.competencia_pagamento}
                      <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                        {c.arquivo_pagamento}
                      </div>
                    </td>
                    <td className="ff-mono text-end">
                      {brl(c.valor_pago_no_retorno)}
                      {c.excede && (
                        <div>
                          <Badge tom="pendente">maior que a glosa</Badge>
                        </div>
                      )}
                    </td>
                    <td className="ff-mono text-end" style={{ fontWeight: 600 }}>
                      {brl(c.valor_sugerido)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            <Paginacao {...pag} rotulo="candidatos" />
          </>
        )}
      </Painel>
    </>
  );
}
